import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { ActionsModal } from '../../components/shared/ActionsModal';
import { ReactComponent as CheckIcon } from '../../react-icons/check.svg';
import '../../styles/DataPrivacySettings.css';
import { fetchAppConfig } from '../../services/config';
import { showMessage } from '../../showMessage';

import type { ModalAction } from '../../components/shared/ActionsModal';

type AppConfig = { share_data: boolean; allow_analytics: boolean };
function isAppConfig(value: unknown): value is AppConfig {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return typeof v.share_data === 'boolean' && typeof v.allow_analytics === 'boolean';
}

type ApiResponse = { success: boolean; message?: string };
function isApiResponse(value: unknown): value is ApiResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const hasSuccess = typeof v.success === 'boolean';
  const hasMessage = v.message === undefined || typeof v.message === 'string';
  return hasSuccess && hasMessage;
}

// Reused i18n keys
const NOTIF_ERROR = 'notifications.error';
const NOTIF_SUCCESS = 'notifications.success';
const ERR_MISSING_TOKEN = 'errors.missingToken';
const ERR_MISSING_API = 'errors.missingApiBase';
const MODALS_UNEXPECTED_ERROR = 'modals.unexpectedError';
const SETTINGS_PRIVACY_DELETE_ACCOUNT = 'settings.privacy.deleteAccount';

async function performDeleteAccount(t: (k: string) => string): Promise<void> {
  const token = localStorage.getItem('token');
  if (!token) {
    showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_TOKEN));
    return;
  }
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!baseUrl) {
    showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_API));
    return;
  }
  try {
    const response = await fetch(`${baseUrl}/api/user/delete-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const json: unknown = await response.json();
    if (isApiResponse(json) && json.success) {
      showMessage('success', t(NOTIF_SUCCESS), t(SETTINGS_PRIVACY_DELETE_ACCOUNT) + ' ' + t(NOTIF_SUCCESS).toLowerCase());
      localStorage.removeItem('token');
      window.location.href = '/authUser';
    } else {
      const message =
        isApiResponse(json) && json.message
          ? json.message
          : t('common.delete') + ' ' + t(NOTIF_ERROR).toLowerCase();
      showMessage('error', t(NOTIF_ERROR), message);
    }
  } catch (error) {
    console.error('Error deleting account:', error);
    showMessage('error', t(NOTIF_ERROR), t(MODALS_UNEXPECTED_ERROR));
  }
}

export function DataPrivacySettings() {
  const { t } = useTranslation('common');
  const [privacySettings, setPrivacySettings] = useState({
    shareData: false,
    analytics: false,
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const cfgUnknown: unknown = await fetchAppConfig();
        if (isAppConfig(cfgUnknown)) {
          setPrivacySettings({
            shareData: cfgUnknown.share_data,
            analytics: cfgUnknown.allow_analytics,
          });
        }
      } catch (error) {
        console.error('Error fetching app config:', error);
      }
    })();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setPrivacySettings((previous) => ({ ...previous, [name]: checked }));
  };

  const updateSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_TOKEN));
      return;
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_API));
      return;
    }
    try {
      const response = await fetch(`${baseUrl}/api/user/updateSettings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shareData: privacySettings.shareData,
          allowAnalytics: privacySettings.analytics,
        }),
      });
      const json: unknown = await response.json();
      if (isApiResponse(json) && json.success) {
        showMessage('success', t(NOTIF_SUCCESS), t('settings.privacy.settingsUpdated'));
        // Refrescar configuración completa
        try {
          const cfgUnknown: unknown = await fetchAppConfig();
          if (isAppConfig(cfgUnknown)) {
            setPrivacySettings({
              shareData: cfgUnknown.share_data,
              analytics: cfgUnknown.allow_analytics,
            });
          }
        } catch (error) {
          console.error('Error refreshing app config:', error);
        }
      } else {
        const message = isApiResponse(json) && json.message ? json.message : t('settings.privacy.failedUpdate');
        showMessage('error', t(NOTIF_ERROR), message);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      showMessage('error', t(NOTIF_ERROR), t('modals.unexpectedError'));
    }
  };

  const openDeleteModal = () => {
    setIsDeleteModalVisible(true);
  };

  const modalActions: ModalAction[] = [
    {
      label: t('common.cancel'),
      onClick: () => {
        setIsDeleteModalVisible(false);
      },
      styleType: 'secondary',
    },
    {
      label: t('common.delete'),
      onClick: () => {
        setIsDeleteModalVisible(false);
        void performDeleteAccount(t);
      },
      styleType: 'danger',
    },
  ];

  return (
    <div className="settings-form-container-privacy">
      <h2 className="settings-form-title-privacy">{t('settings.privacy.title')}</h2>

      <div className="checkbox-group-privacy">
        <label>
          <input
            type="checkbox"
            name="shareData"
            checked={privacySettings.shareData}
            onChange={handleChange}
          />
          <span className="checkmark">
            <CheckIcon className="palomilla-icon" />
          </span>
          {t('settings.privacy.shareData')}
        </label>
        <p className="form-hint-privacy">{t('settings.privacy.shareDataHint')}</p>
      </div>

      <div className="checkbox-group-privacy">
        <label>
          <input
            type="checkbox"
            name="analytics"
            checked={privacySettings.analytics}
            onChange={handleChange}
          />
          <span className="checkmark">
            <CheckIcon className="palomilla-icon" />
          </span>
          {t('settings.privacy.allowAnalytics')}
        </label>
      </div>

      <div className="privacy-actions-privacy">
        <h3>{t('settings.privacy.dataManagement')}</h3>
        <button className="delete-account-btn-privacy" onClick={openDeleteModal}>
          {t('settings.privacy.deleteAccount')}
        </button>
      </div>

      <div className="privacy-actions-privacy">
        <button className="save-settings-btn-privacy" onClick={updateSettings}>
          {t('settings.privacy.saveChanges')}
        </button>
      </div>

      <ActionsModal
        isVisible={isDeleteModalVisible}
        title={t('settings.privacy.confirmDeletionTitle')}
        message={t('settings.privacy.confirmDeletionMessage')}
        actions={modalActions}
        onClose={() => setIsDeleteModalVisible(false)}
      />
    </div>
  );
}
